import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from '../utils/toast';
import { api } from '../config/api';
import { UserPlus, Save, Loader2, Plus, ArrowLeft, Search, Edit, Trash2 } from 'lucide-react';
import { normalizeDateInput, toDateInputValue, toDateObject } from '../utils/dateFormat';
import { isValidPhoneNumber, normalizePhoneInput } from '../utils/formValidation';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton,
  masterHeader,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanelPadding,
  masterPrimaryButton,
  masterSearchInput,
  masterSearchWrap,
  masterSelect,
  masterSubtitle,
  masterToolbar,
  masterTitle,
  masterTableData
} from '../components/masterPageStyles';

const getInitialFormData = () => ({
  patientId: '',
  appointmentId: '',
  fullName: '',
  gender: '',
  dob: '',
  age: '',
  mobileNumber: '',
  alternateMobile: '',
  villageTown: '',
  postOffice: '',
  policeStation: '',
  district: '',
  stateName: '',
  pinCode: '',
  bloodGroup: '',
  allergies: '',
  knownMedicalConditions: '',
  emergencyContactName: '',
  emergencyContactNumber: '',
  department: '',
  notes: ''
});

const PATIENT_FORM_FIELDS = [
  'appointmentId',
  'fullName',
  'gender',
  'dob',
  'mobileNumber',
  'alternateMobile',
  'villageTown',
  'postOffice',
  'policeStation',
  'district',
  'stateName',
  'pinCode',
  'bloodGroup',
  'allergies',
  'knownMedicalConditions',
  'emergencyContactName',
  'emergencyContactNumber',
  'department',
  'notes'
];

const normalizePatientFieldValue = (value) => {
  if (value === null || value === undefined) return '';
  return typeof value === 'string' ? value.trim() : String(value).trim();
};

const buildPatientPayload = (currentData, baseData, isEditMode) => {
  const payload = {};

  PATIENT_FORM_FIELDS.forEach((field) => {
    const currentValue = normalizePatientFieldValue(currentData[field]);
    const baseValue = normalizePatientFieldValue(baseData?.[field]);
    const changed = currentValue !== baseValue;

    if (!isEditMode) {
      if (!currentValue) return;
      if (field === 'gender' && currentValue === baseValue) return;
      payload[field] = currentValue;
      return;
    }

    if (!changed) return;
    payload[field] = currentValue || null;
  });

  payload.fullName = normalizePatientFieldValue(currentData.fullName);
  payload.mobileNumber = normalizePatientFieldValue(currentData.mobileNumber);
  payload.department = normalizePatientFieldValue(currentData.department);

  return payload;
};

const calculateAge = (dob) => {
  if (!dob) return '';
  const birthDate = toDateObject(dob);
  if (!birthDate) return '';

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) age -= 1;
  return age < 0 ? 0 : age;
};

const PatientsRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingId, setLoadingId] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(true);
  const [editingPatientId, setEditingPatientId] = useState(null);
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(getInitialFormData());
  const [initialFormData, setInitialFormData] = useState(getInitialFormData());
  const [activeSection, setActiveSection] = useState('details');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [visitHistory, setVisitHistory] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [pharmacyHistory, setPharmacyHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [pharmacyForm, setPharmacyForm] = useState({
    billNo: '',
    medicinesPurchased: '',
    amount: ''
  });
  const [savingHistory, setSavingHistory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const isEditMode = Boolean(editingPatientId);

  const fetchNextPatientId = async () => {
    setLoadingId(true);
    try {
      const res = await api.get(`/patients/latest-id`);
      if (res.data.success) {
        setFormData((prev) => ({ ...prev, patientId: res.data.nextId }));
        setInitialFormData((prev) => ({ ...prev, patientId: res.data.nextId }));
      }
    } catch (err) {
      toast.error('FAILED TO LOAD PATIENT ID');
    } finally {
      setLoadingId(false);
    }
  };

  useEffect(() => {
    fetchNextPatientId();
  }, []);

  const fetchPatients = async () => {
    setLoadingList(true);
    try {
      const res = await api.get(`/patients/all`);
      if (res.data.success) {
        setPatients(res.data.data || []);
      }
    } catch (err) {
      toast.error('FAILED TO LOAD PATIENTS');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchAppointments = async () => {
    try {
      const res = await api.get(`/appointments/all`);
      if (res.data.success) {
        setAppointments(res.data.data || []);
      }
    } catch (err) {
      toast.error('FAILED TO LOAD APPOINTMENTS');
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await api.get(`/master/department/all`);
      if (res.data.success) {
        const deptList = res.data.data || [];
        setDepartments(deptList);
      }
    } catch (err) {
      toast.error('FAILED TO LOAD DEPARTMENTS');
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDoctors = async () => {
    try {
      const res = await api.get(`/doctors/all`);
      if (res.data.success) {
        setDoctors(res.data.data || []);
      }
    } catch (err) {
      toast.error('FAILED TO LOAD DOCTORS');
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    const prefillAppointment = location.state?.prefillFromAppointment;
    const prefillPatientProfile = location.state?.prefillPatientProfile;
    if (!prefillAppointment) return;

    setActiveSection('details');
    setSelectedPatient(prefillPatientProfile || null);
    setEditingPatientId(prefillPatientProfile?._id || null);
    setFormData((prev) => {
      if (prefillPatientProfile) {
        const nextFormData = {
          patientId: prefillPatientProfile.patientId || prev.patientId,
          appointmentId: prefillAppointment.appointmentId || prefillPatientProfile.appointmentId || '',
          fullName: prefillPatientProfile.fullName || prefillAppointment.patientName || '',
          gender: prefillPatientProfile.gender || ' ',
          dob: toDateInputValue(prefillPatientProfile.dob || ''),
          age: prefillPatientProfile.age ?? '',
          mobileNumber: prefillPatientProfile.mobileNumber || prefillAppointment.patientMobile || '',
          alternateMobile: prefillPatientProfile.alternateMobile || '',
          villageTown: prefillPatientProfile.villageTown || '',
          postOffice: prefillPatientProfile.postOffice || '',
          policeStation: prefillPatientProfile.policeStation || '',
          district: prefillPatientProfile.district || '',
          stateName: prefillPatientProfile.stateName || '',
          pinCode: prefillPatientProfile.pinCode || '',
          bloodGroup: prefillPatientProfile.bloodGroup || '',
          allergies: prefillPatientProfile.allergies || '',
          knownMedicalConditions: prefillPatientProfile.knownMedicalConditions || '',
          emergencyContactName: prefillPatientProfile.emergencyContactName || '',
          emergencyContactNumber: prefillPatientProfile.emergencyContactNumber || '',
          department: prefillAppointment.department || prefillPatientProfile.department || '',
          notes: prefillAppointment.notes || prefillPatientProfile.notes || ''
        };
        setInitialFormData(nextFormData);
        return nextFormData;
      }

      const nextFormData = {
        ...getInitialFormData(),
        patientId: prev.patientId,
        appointmentId: prefillAppointment.appointmentId || '',
        fullName: prefillAppointment.patientName || '',
        mobileNumber: prefillAppointment.patientMobile || '',
        department: prefillAppointment.department || '',
        notes: prefillAppointment.notes || ''
      };
      setInitialFormData(getInitialFormData());
      return nextFormData;
    });
    setShowForm(true);
    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, age: calculateAge(prev.dob) }));
  }, [formData.dob]);

  const maxDob = useMemo(() => toDateInputValue(new Date()), []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'appointmentId') {
      const selected = appointments.find((appt) => appt.appointmentId === value);
      setFormData((prev) => ({
        ...prev,
        appointmentId: value,
        fullName: selected?.patientName || prev.fullName,
        mobileNumber: selected?.patientMobile || prev.mobileNumber,
        department: selected?.department || prev.department
      }));
      return;
    }
    let nextValue = value;
    if (name === 'dob') nextValue = normalizeDateInput(value);
    if (['mobileNumber', 'alternateMobile', 'emergencyContactNumber'].includes(name)) {
      nextValue = normalizePhoneInput(value);
    }
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const openAddForm = async () => {
    setActiveSection('details');
    setSelectedPatient(null);
    setEditingPatientId(null);
    const nextInitialFormData = getInitialFormData();
    setFormData(nextInitialFormData);
    setInitialFormData(nextInitialFormData);
    setShowForm(true);
    await fetchNextPatientId();
  };

  const closeForm = async () => {
    setShowForm(true);
    setEditingPatientId(null);
    const nextInitialFormData = getInitialFormData();
    setFormData(nextInitialFormData);
    setInitialFormData(nextInitialFormData);
    setActiveSection('details');
    await fetchNextPatientId();
  };

  const handleEdit = (patient) => {
    setSelectedPatient(patient);
    setActiveSection('details');
    setEditingPatientId(patient._id);
    const nextFormData = {
      patientId: patient.patientId || '',
      appointmentId: patient.appointmentId || '',
      fullName: patient.fullName || '',
      gender: patient.gender || ' ',
      dob: toDateInputValue(patient.dob || ''),
      age: patient.age ?? '',
      mobileNumber: patient.mobileNumber || '',
      alternateMobile: patient.alternateMobile || '',
      villageTown: patient.villageTown || '',
      postOffice: patient.postOffice || '',
      policeStation: patient.policeStation || '',
      district: patient.district || '',
      stateName: patient.stateName || '',
      pinCode: patient.pinCode || '',
      bloodGroup: patient.bloodGroup || '',
      allergies: patient.allergies || '',
      knownMedicalConditions: patient.knownMedicalConditions || '',
      emergencyContactName: patient.emergencyContactName || '',
      emergencyContactNumber: patient.emergencyContactNumber || '',
      department: patient.department || '',
      notes: patient.notes || ''
    };
    setFormData(nextFormData);
    setInitialFormData(nextFormData);
    setShowForm(true);
  };

  const handleDelete = async (patient) => {
    const confirmed = window.confirm(`Delete ${patient.fullName} (${patient.patientId})?`);
    if (!confirmed) return;
    try {
      const res = await api.delete(`/patients/${patient._id}`);
      if (res.data.success) {
        toast.success('PATIENT DELETED');
        await fetchPatients();
        if (selectedPatient?._id === patient._id) setSelectedPatient(null);
      } else {
        toast.error('DELETE FAILED');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'DELETE FAILED');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submitAction = e.nativeEvent?.submitter?.dataset?.action || 'save';

    if (!formData.fullName.trim()) return toast.error('FULL NAME IS REQUIRED');
    if (!formData.mobileNumber.trim()) return toast.error('MOBILE NUMBER IS REQUIRED');
    if (!isValidPhoneNumber(formData.mobileNumber)) return toast.error('MOBILE NUMBER MUST BE EXACTLY 10 DIGITS');
    if (formData.emergencyContactNumber && !isValidPhoneNumber(formData.emergencyContactNumber)) return toast.error('EMERGENCY NUMBER MUST BE EXACTLY 10 DIGITS');
    if (!formData.department) return toast.error('DEPARTMENT IS REQUIRED');

    setSaving(true);
    try {
      const payload = buildPatientPayload(formData, initialFormData, isEditMode);

      const res = isEditMode
        ? await api.put(`/patients/update/${editingPatientId}`, payload)
        : await api.post(`/patients/register`, payload);
      if (res.data.success) {
        const savedPatient = res.data.data;
        toast.success(isEditMode ? `PATIENT UPDATED | ID ${savedPatient.patientId}` : `PATIENT REGISTERED | ID ${savedPatient.patientId}`);
        await fetchPatients();
        await closeForm();
        if (submitAction === 'next') {
          navigate('/ConsultationBilling', {
            state: {
              prefillFromPatient: savedPatient
            }
          });
        }
      } else {
        toast.error(isEditMode ? 'UPDATE FAILED' : 'REGISTRATION FAILED');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || (isEditMode ? 'UPDATE FAILED' : 'REGISTRATION FAILED'));
    } finally {
      setSaving(false);
    }
  };

  const filteredPatients = useMemo(() => {
    const sortedList = [...patients].sort((a, b) => {
      const idA = String(a.patientId || a._id || '');
      const idB = String(b.patientId || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const name = (item.fullName || '').toLowerCase();
      const mobile = (item.mobileNumber || '').toLowerCase();
      const patientId = (item.patientId || '').toLowerCase();

      return name.startsWith(q) || mobile.startsWith(q) || patientId.startsWith(q);
    });
  }, [patients, searchTerm]);

  const paginatedPatients = useMemo(() => {
    if (pageSize === -1) return filteredPatients;
    const start = (currentPage - 1) * pageSize;
    return filteredPatients.slice(start, start + pageSize);
  }, [filteredPatients, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filteredPatients.length, pageSize]);

  const loadHistory = async (type, patientMongoId) => {
    if (!patientMongoId) return;
    setLoadingHistory(true);
    try {
      const res = await api.get(`/patients/${patientMongoId}/${type}-history`);
      if (res.data.success) {
        if (type === 'visit') setVisitHistory(res.data.data || []);
        if (type === 'billing') setBillingHistory(res.data.data || []);
        if (type === 'pharmacy') setPharmacyHistory(res.data.data || []);
      }
    } catch (err) {
      toast.error('FAILED TO LOAD HISTORY');
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (!selectedPatient) return;
    if (activeSection === 'visit') loadHistory('visit', selectedPatient._id);
    if (activeSection === 'billing') loadHistory('billing', selectedPatient._id);
    if (activeSection === 'pharmacy') loadHistory('pharmacy', selectedPatient._id);
  }, [activeSection, selectedPatient]);

  const resetPharmacyForm = () =>
    setPharmacyForm({ billNo: '', medicinesPurchased: '', amount: '' });

  const handlePharmacyChange = (e) => {
    const { name, value } = e.target;
    setPharmacyForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitPharmacyHistory = async (e) => {
    e.preventDefault();
    if (!selectedPatient) return toast.error('SELECT A PATIENT');
    if (!pharmacyForm.billNo || !pharmacyForm.medicinesPurchased) return toast.error('BILL NO AND MEDICINES REQUIRED');
    setSavingHistory(true);
    try {
      const payload = {
        billNo: pharmacyForm.billNo,
        medicinesPurchased: pharmacyForm.medicinesPurchased,
        amount: pharmacyForm.amount ? Number(pharmacyForm.amount) : 0
      };
      const res = await api.post(`/patients/${selectedPatient._id}/pharmacy-history`, payload);
      if (res.data.success) {
        toast.success('PHARMACY HISTORY SAVED');
        resetPharmacyForm();
        await loadHistory('pharmacy', selectedPatient._id);
      } else {
        toast.error('SAVE FAILED');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'SAVE FAILED');
    } finally {
      setSavingHistory(false);
    }
  };

  const historyEmptyState = (message) => (
    <div className="py-12 text-center text-slate-500 font-bold uppercase">{message}</div>
  );

  const sectionTabs = [
    { id: 'details', label: 'Patients Details' },
    { id: 'visit', label: 'Visit History' },
    { id: 'billing', label: 'Billing History' },
    { id: 'pharmacy', label: 'Pharmacy History' }
  ];

 return (
    <div className={masterPageShell}>
      <header className={masterHeader}>
        <div className={masterHeaderCard}>
          <div className="flex items-center gap-4">
            <div className={masterHeaderIcon}>
              <UserPlus size={18} />
            </div>
            <div>
              <h1 className={masterTitle}>Patients Registration</h1>
              <p className={masterSubtitle}>Create New Patient Profile</p>
            </div>
          </div>

         <button
            type="button"
            onClick={() => setShowForm(!showForm)}
            className={showForm ? masterPrimaryButton : masterAccentButton}
          >
            {showForm ? <ArrowLeft size={14} /> : <Plus size={14} />}
            {showForm ? 'VIEW REGISTERED LIST' : 'OPEN REGISTRATION FORM'}
          </button>
        </div>
      </header>

      <main className="px-2 pb-2">
        {isEditMode && (
          <div className="max-w-7xl mx-auto mb-3">
            <div className="bg-white rounded-xl p-1 shadow-sm border border-slate-400 flex flex-wrap gap-1">
              {sectionTabs.map((tab) => {
                const isActive = activeSection === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveSection(tab.id)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-bold tracking-wide transition-all ${
                      isActive ? 'bg-emerald-500 text-white shadow-sm' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {isEditMode && selectedPatient && (
          <div className="max-w-7xl mx-auto mb-3">
            {/* <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2 flex items-center justify-between">
              <div className="text-[11px] text-emerald-900 font-bold uppercase">
                Patient: {selectedPatient.fullName} ({selectedPatient.patientId})
              </div>
              <button
                type="button"
                onClick={() => setSelectedPatient(null)}
                className="text-[9px] font-bold uppercase text-rose-500 underline"
              >
                Clear Selection
              </button>
            </div> */}
          </div>
        )}

        {!isEditMode && !showForm ? (
          <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
              <div className="border-b border-slate-200 flex-shrink-0">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(val);
                    setCurrentPage(1);
                  }} 
                  placeholder="Search alphabetically by name, mobile, or ID..." 
                  totalItems={filteredPatients.length} 
                  currentPage={currentPage} 
                  pageSize={pageSize} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={setPageSize} 
                />
              </div>
    <div className="max-h-[calc(100vh-250px)] overflow-auto">
            <table className="w-full min-w-[1000px] table-fixed text-left text-[10px]">
              <thead className="sticky top-0 z-10 bg-slate-100 text-[9px] font-extrabold uppercase tracking-[0.14em] text-emerald-600 border-b border-slate-400">
                    <tr>
                      <th className={masterTableData}>S.No</th>
                      <th className={masterTableData}>ID</th>
                      <th className={masterTableData}>Name</th>
                      <th className={masterTableData}>Mobile</th>
                      <th className={masterTableData}>Gender</th>
                      <th className={masterTableData}>Department</th>
                      <th className={masterTableData}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-300 font-bold text-slate-700">
                    {paginatedPatients.length > 0 ? paginatedPatients.map((patient, index) => (
                      <tr
                        key={patient._id}
                        onClick={() => setSelectedPatient(patient)}
                        className={`cursor-pointer hover:bg-emerald-50/30 ${
                          selectedPatient?.patientId === patient.patientId ? 'bg-emerald-50/40' : ''
                        }`}
                      >
                        <td className={masterTableData}>{pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}</td>
                        <td className={masterTableData}>{patient.patientId}</td>
                        <td className={masterTableData} title={patient.fullName || '-'}>{patient.fullName || '-'}</td>
                        <td className={masterTableData}>{patient.mobileNumber || '-'}</td>
                        <td className={masterTableData}>{patient.gender || '-'}</td>
                        <td className={masterTableData}>{patient.department || '-'}</td>
                        <td className={masterTableData}>
                          <div className="flex justify-center gap-1">
                            <button onClick={(e) => { e.stopPropagation(); handleEdit(patient); }} className="p-1 text-emerald-600 bg-slate-100 rounded-lg hover:bg-emerald-600 hover:text-white transition-colors">
                              <Edit size={14} />
                              </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDelete(patient); }} className="p-1 text-rose-600 bg-rose-50 rounded-lg hover:bg-rose-600 hover:text-white transition-colors">
                              <Trash2 size={14} />
                              </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="7" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">No matching records found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
            </div>
          </div>
        ) : activeSection === 'details' ? (
          <form onSubmit={handleSubmit} className="mx-auto max-w-7xl space-y-1">
            <div className={masterPanelPadding}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-1">
              {/* <InputField label="Patient ID (Auto)" name="patientId" value={loadingId ? '...' : formData.patientId} readOnly /> */}
              <SelectField label="Appointment Reference" name="appointmentId" value={formData.appointmentId} onChange={handleChange} required={false}>
                <option value="">-- NONE --</option>
                {appointments.map((appt) => <option key={appt._id} value={appt.appointmentId}>{appt.appointmentId} | {appt.patientName}</option>)}
              </SelectField>
              <InputField label="Full Name" name="fullName" value={formData.fullName} onChange={handleChange} required />
              <SelectField label="Gender" name="gender" value={formData.gender} onChange={handleChange}>
                 <option value="">-- SELECT --</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </SelectField>
            </div>
            </div>

            <div className={masterPanelPadding}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-1">
              <InputField
                label="DOB"
                type="text"
                name="dob"
                value={formData.dob}
                onChange={handleChange}
                inputMode="numeric"
                maxLength={10}
                placeholder="DD/MM/YYYY"
                max={maxDob}
                required={false}
              />
              <InputField label="Age (Auto)" name="age" value={formData.age} readOnly required={false} />
              <InputField label="Mobile Number" name="mobileNumber" value={formData.mobileNumber} onChange={handleChange} required />
              {/* <InputField label="Alt Mobile" name="alternateMobile" value={formData.alternateMobile} onChange={handleChange} required={false} /> */}
         
              <InputField label="Emergency Contact Name" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} required={false} />
              <InputField label="Emergency Number" name="emergencyContactNumber" value={formData.emergencyContactNumber} onChange={handleChange} required={false} />
              <SelectField label="Blood Group" name="bloodGroup" value={formData.bloodGroup} onChange={handleChange} required={false}>
                <option value="">-- SELECT --</option>
                {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
              </SelectField>
           
              <SelectField label="Department" name="department" value={formData.department} onChange={handleChange}>
                <option value="">-- SELECT --</option>
                {departments.map((dept) => <option key={dept._id} value={dept.deptName}>{dept.deptName}</option>)}
              </SelectField>
              <InputField label="Notes" name="notes" value={formData.notes} onChange={handleChange} required={false} />
            </div>
            </div>

            <div className={masterPanelPadding}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
              <InputField label="Vill / Town" name="villageTown" value={formData.villageTown} onChange={handleChange} required={false} />
              <InputField label="PO" name="postOffice" value={formData.postOffice} onChange={handleChange} required={false} />
              <InputField label="PS" name="policeStation" value={formData.policeStation} onChange={handleChange} required={false} />
              <InputField label="District" name="district" value={formData.district} onChange={handleChange} required={false} />
              <InputField label="State" name="stateName" value={formData.stateName} onChange={handleChange} required={false} />
              <InputField label="PIN" name="pinCode" value={formData.pinCode} onChange={handleChange} required={false} />
            </div>
            </div>

            <div className={masterPanelPadding}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              <TextAreaField label="Allergies" name="allergies" value={formData.allergies} onChange={handleChange} />
              <TextAreaField label="Medical Conditions" name="knownMedicalConditions" value={formData.knownMedicalConditions} onChange={handleChange} />
            </div>
            </div>

            <div className={masterPanelPadding}>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <button
                  type="submit"
                  disabled={saving || loadingId}
                  data-action="save"
                  className={`${masterAccentButton} inline-flex w-full items-center justify-center gap-2 py-1 disabled:opacity-50`}
                >
                  {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                  {isEditMode ? 'UPDATE PATIENT' : 'SAVE PATIENT'}
                </button>
                <button
                  type="submit"
                  disabled={saving || loadingId}
                  data-action="next"
                  className={`${masterPrimaryButton} inline-flex w-full items-center justify-center gap-2 py-1 disabled:opacity-50`}
                >
                  {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                  {isEditMode ? 'UPDATE & NEXT' : 'SUBMIT & NEXT'}
                </button>
              </div>
            </div>
          </form>
        ) : isEditMode && activeSection === 'visit' ? (
          <div className="mx-auto max-w-7xl space-y-1">
            <div className={`${masterPanelPadding} overflow-hidden`}>
              <h2 className="border-b pb-2 text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Visit History</h2>
              {loadingHistory ? (
                <div className="py-8 text-center text-[11px] font-bold uppercase text-slate-400">Loading visit history...</div>
              ) : visitHistory.length > 0 ? (
                <div className="mt-3 overflow-auto">
                  <table className="w-full min-w-[900px] table-fixed text-left text-[10px]">
                    <thead className="bg-slate-100 text-[9px] font-extrabold uppercase tracking-[0.14em] text-emerald-600">
                      <tr>
                        <th className={masterTableData}>S.No</th>
                        <th className={masterTableData}>Date</th>
                        <th className={masterTableData}>Appointment</th>
                        <th className={masterTableData}>Invoice</th>
                        <th className={masterTableData}>Doctor</th>
                        <th className={masterTableData}>Department</th>
                        <th className={masterTableData}>Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-300 font-bold text-slate-700">
                      {visitHistory.map((item, index) => (
                        <tr key={`${item._id || item.invoiceNo || item.appointmentId || index}`} className="hover:bg-emerald-50/30">
                          <td className={masterTableData}>{index + 1}</td>
                          <td className={masterTableData}>{item.visitDate || '-'}</td>
                          <td className={masterTableData}>{item.appointmentId || '-'}</td>
                          <td className={masterTableData}>{item.invoiceNo || '-'}</td>
                          <td className={masterTableData}>{item.doctorName || '-'}</td>
                          <td className={masterTableData}>{item.department || '-'}</td>
                          <td className={masterTableData}>{item.status || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : historyEmptyState('No visit history found')}
            </div>
          </div>
        ) : isEditMode && activeSection === 'billing' ? (
          <div className="mx-auto max-w-7xl space-y-3">
            <div className={`${masterPanelPadding} overflow-hidden`}>
              <h2 className="border-b pb-2 text-[10px] font-extrabold uppercase tracking-[0.12em] text-slate-400">Billing History</h2>
              {loadingHistory ? (
                <div className="py-8 text-center text-[11px] font-bold uppercase text-slate-400">Loading billing history...</div>
              ) : billingHistory.length > 0 ? (
                <div className="mt-3 overflow-auto">
                  <table className="w-full min-w-[1000px] table-fixed text-left text-[10px]">
                    <thead className="bg-slate-100 text-[9px] font-extrabold uppercase tracking-[0.14em] text-emerald-600">
                      <tr>
                        <th className={masterTableData}>S.No</th>
                        <th className={masterTableData}>Invoice</th>
                        <th className={masterTableData}>Bill Date</th>
                        <th className={masterTableData}>Appointment</th>
                        <th className={masterTableData}>Doctor</th>
                        <th className={masterTableData}>Department</th>
                        <th className={masterTableData}>Amount</th>
                        <th className={masterTableData}>Payment</th>
                        <th className={masterTableData}>Mode</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-300 font-bold text-slate-700">
                      {billingHistory.map((item, index) => (
                        <tr key={`${item._id || item.invoiceNo || index}`} className="hover:bg-emerald-50/30">
                          <td className={masterTableData}>{index + 1}</td>
                          <td className={masterTableData}>{item.invoiceNo || '-'}</td>
                          <td className={masterTableData}>{item.billDate || '-'}</td>
                          <td className={masterTableData}>{item.appointmentId || '-'}</td>
                          <td className={masterTableData}>{item.doctorName || '-'}</td>
                          <td className={masterTableData}>{item.department || '-'}</td>
                          <td className={masterTableData}>{Number(item.amount || 0).toFixed(2)}</td>
                          <td className={masterTableData}>{item.paymentStatus || '-'}</td>
                          <td className={masterTableData}>{item.paymentMode || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : historyEmptyState('No billing history found')}
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
};

/* COMPACT COMPONENTS */
const InputField = ({ label, required = false, ...props }) => (
  <div className="flex flex-col ">
    <label className={masterInputLabel}>{label}</label>
    <input
      required={required}
      {...props}
      className={masterInput}
    />
  </div>
);

const SelectField = ({ label, children, required = true, ...props }) => (
  <div className="flex flex-col ">
    <label className={masterInputLabel}>{label}</label>
    <select
      required={required}
      {...props}
      className={masterSelect}
    >
      {children}
    </select>
  </div>
);

const TextAreaField = ({ label, rows = 2, ...props }) => (
  <div className="flex flex-col ">
    <label className={masterInputLabel}>{label}</label>
    <textarea
      rows={rows}
      {...props}
      className={`${masterInput} resize-y`}
    />
  </div>
);

export default PatientsRegistration;

